import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Store, MapPin, Clock, Camera, Eye, Plus, X, Loader2, UploadCloud, Check } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

const SIGNAL_TYPE_LABEL = {
  shelf_expansion: "Shelf expansion",
  endcap_placement: "Endcap",
  new_sku: "New SKU",
  out_of_stock_velocity: "OOS velocity",
  diaspora_cluster: "Diaspora cluster",
  viral_aisle: "Viral aisle",
  regional_test: "Regional test",
};

const SIGNAL_TYPE_COLOR = {
  shelf_expansion: "border-bm-cyan/40 text-bm-cyan",
  endcap_placement: "border-bm-amber/40 text-bm-amber",
  new_sku: "border-bm-green/40 text-bm-green",
  out_of_stock_velocity: "border-bm-danger/40 text-bm-danger",
  diaspora_cluster: "border-purple-400/40 text-purple-300",
  viral_aisle: "border-pink-400/40 text-pink-300",
  regional_test: "border-blue-300/40 text-blue-300",
};

function OfflineCard({ o, idx }) {
  const cls = SIGNAL_TYPE_COLOR[o.signal_type] || "border-white/15 text-bm-muted";
  return (
    <div
      data-testid={`offline-card-${o.id}`}
      className="bm-card fade-up p-4 flex flex-col gap-3"
      style={{ animationDelay: `${Math.min(idx * 40, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`label-mono px-1.5 py-0.5 border ${cls}`}>{SIGNAL_TYPE_LABEL[o.signal_type] || o.signal_type}</span>
          <span className="label-mono text-bm-dim flex items-center gap-1">
            <Store className="w-3 h-3" />{o.retailer}
          </span>
        </div>
        <div className="font-mono text-[10px] text-bm-dim">{o.observed_date}</div>
      </div>

      <h3 className="font-heading text-base font-bold leading-tight text-white" data-testid={`offline-observation-${o.id}`}>
        {o.observation}
      </h3>

      {o.photo_url && (
        <div className="relative aspect-[3/2] overflow-hidden bg-bm-elevated">
          <img src={o.photo_url} alt={o.observation} className="w-full h-full object-cover opacity-90" />
          <div className="absolute bottom-1 left-1 flex items-center gap-1 px-1.5 py-0.5 bg-black/70 text-[9px] font-mono text-white">
            <Camera className="w-2.5 h-2.5" />FIELD CAPTURE
          </div>
        </div>
      )}

      <div className="flex items-start gap-1 text-xs text-bm-muted">
        <MapPin className="w-3 h-3 text-bm-amber shrink-0 mt-0.5" />
        <span>{o.location} · {o.geography}</span>
      </div>

      {o.notes && <p className="text-xs leading-relaxed text-bm-text/85">{o.notes}</p>}

      {o.reference_brands?.length > 0 && (
        <div className="border-t border-white/5 pt-2">
          <div className="label-mono text-bm-dim mb-1">BRANDS ON SHELF</div>
          <div className="flex flex-wrap gap-1">
            {o.reference_brands.map((b) => (
              <span key={b} className="text-[11px] font-mono text-bm-amber border border-bm-amber/30 px-1.5 py-0.5">
                {b}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px] font-mono pt-2 border-t border-white/5">
        <div className="flex items-center gap-1 text-bm-muted">
          <Eye className="w-3 h-3" />{o.observer}
        </div>
        <div className="flex items-center gap-1 text-bm-dim" data-testid={`offline-generated-at-${o.id}`}>
          <Clock className="w-3 h-3" />Logged {formatGeneratedAt(o.generated_at || o.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function OfflineSignals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retailerFilter, setRetailerFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = (rf) => {
    setLoading(true);
    api.offlineSignals(rf ? { retailer: rf } : {}).then((d) => {
      setItems(d.items || []);
      setLoading(false);
    });
  };

  useEffect(() => { load(retailerFilter); }, [retailerFilter]);

  const allRetailers = Array.from(new Set(items.map((i) => i.retailer)));

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber">OFFLINE SIGNALS · PHYSICAL-SHELF INTEL</div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            What's moving on the shelf.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Field-captured retail signals — Costco endcaps, Olive Young shelf wins, Don Quijote new placements,
            Erewhon viral aisles, diaspora retail clusters, Tier-2 regional tests. Leading indicators that often
            pre-date online virality by 2-6 months.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="open-upload-btn"
            className="flex items-center gap-2 px-3 h-10 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />LOG OBSERVATION
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="retailer-filters">
        <button
          onClick={() => setRetailerFilter("")}
          data-testid="retailer-filter-all"
          className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
            !retailerFilter ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
          }`}
        >
          ALL
        </button>
        {allRetailers.slice(0, 8).map((r) => (
          <button
            key={r}
            onClick={() => setRetailerFilter(r)}
            data-testid={`retailer-filter-${r.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
              retailerFilter === r ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING OFFLINE SIGNALS...</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No offline signals matching filter.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="offline-grid">
        {items.map((o, i) => <OfflineCard key={o.id} o={o} idx={i} />)}
      </div>

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onCreated={() => { setUploadOpen(false); load(retailerFilter); }}
      />
    </div>
  );
}


// ============ UPLOAD MODAL ============
const SIGNAL_TYPES = ["shelf_expansion", "endcap_placement", "new_sku", "out_of_stock_velocity", "diaspora_cluster", "viral_aisle", "regional_test"];
const CATEGORIES = ["Skincare", "Color Cosmetics", "Suncare", "Haircare", "Fragrance", "Wellness", "Functional Beverages", "Beauty Tools", "Bodycare"];

function UploadModal({ open, onClose, onCreated }) {
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    observation: "",
    retailer: "",
    location: "",
    geography: "",
    category: "Skincare",
    signal_type: "shelf_expansion",
    observer: "",
    notes: "",
    photo_url: "",
    reference_brands_csv: "",
  });

  if (!open) return null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      setError("Image too large (max 2MB).");
      return;
    }
    setError(null);
    setUploadingPhoto(true);
    try {
      const r = await api.uploadOfflineSignalPhoto(f);
      // Backend returns an /api/files/... path — prefix with REACT_APP_BACKEND_URL so the <img> resolves.
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r.url}`;
      setForm((p) => ({ ...p, photo_url: fullUrl }));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Photo upload failed.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!form.observation.trim()) { setError("Observation is required."); return; }
    if (!form.retailer.trim()) { setError("Retailer is required."); return; }
    setSubmitting(true);
    try {
      const payload = {
        observation: form.observation.trim(),
        retailer: form.retailer.trim(),
        location: form.location.trim(),
        geography: form.geography.trim(),
        category: form.category,
        signal_type: form.signal_type,
        observer: form.observer.trim() || "Field scout",
        notes: form.notes,
        photo_url: form.photo_url || null,
        reference_brands: form.reference_brands_csv.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.createOfflineSignal(payload);
      onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="upload-modal">
      <div className="bm-card-elev w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          data-testid="upload-close"
          className="absolute top-4 right-4 text-bm-muted hover:text-white"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="label-mono text-bm-amber mb-1">LOG FIELD OBSERVATION</div>
        <h2 className="font-heading text-2xl font-black tracking-tight">What did you see on the shelf?</h2>
        <p className="text-xs text-bm-muted mt-1">Photos optional but encouraged — files upload to object storage (≤2MB · PNG/JPG/WebP/GIF), or paste a hosted URL.</p>

        <div className="space-y-4 mt-5">
          <Field label="Observation *" htmlFor="obs">
            <textarea
              id="obs"
              value={form.observation}
              onChange={(e) => setForm({ ...form, observation: e.target.value })}
              placeholder="e.g. Beauty of Joseon now occupies endcap at Olive Young Hongdae"
              rows={2}
              data-testid="upload-observation"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan resize-none"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Retailer *">
              <input
                value={form.retailer}
                onChange={(e) => setForm({ ...form, retailer: e.target.value })}
                placeholder="Costco · Olive Young · Don Quijote …"
                data-testid="upload-retailer"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              />
            </Field>
            <Field label="Location">
              <input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Cerritos, CA · Shibuya, Tokyo …"
                data-testid="upload-location"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Geography">
              <input
                value={form.geography}
                onChange={(e) => setForm({ ...form, geography: e.target.value })}
                placeholder="North America · Korea …"
                data-testid="upload-geography"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              />
            </Field>
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="upload-category"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Signal type">
              <select
                value={form.signal_type}
                onChange={(e) => setForm({ ...form, signal_type: e.target.value })}
                data-testid="upload-signal-type"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              >
                {SIGNAL_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Observer">
            <input
              value={form.observer}
              onChange={(e) => setForm({ ...form, observer: e.target.value })}
              placeholder="Your name / team"
              data-testid="upload-observer"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
            />
          </Field>

          <Field label="Brands on shelf (comma-sep)">
            <input
              value={form.reference_brands_csv}
              onChange={(e) => setForm({ ...form, reference_brands_csv: e.target.value })}
              placeholder="Beauty of Joseon, Anua, COSRX"
              data-testid="upload-ref-brands"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
            />
          </Field>

          <Field label="Notes / context">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything else worth knowing — adjacent brands, pricing, traffic, etc."
              rows={2}
              data-testid="upload-notes"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan resize-none"
            />
          </Field>

          <div>
            <div className="label-mono text-bm-dim mb-2 flex items-center gap-2"><UploadCloud className="w-3 h-3" />PHOTO</div>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onFile}
                data-testid="upload-photo-file"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-trigger"
                className="px-3 py-2 text-xs font-mono border border-bm-cyan/40 text-bm-cyan hover:bg-bm-cyan hover:text-black transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {uploadingPhoto ? <Loader2 className="w-3 h-3 animate-spin" /> : <UploadCloud className="w-3 h-3" />}
                {uploadingPhoto ? "UPLOADING…" : "CHOOSE FILE"}
              </button>
              <input
                value={form.photo_url || ""}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                placeholder="…or paste an image URL"
                data-testid="upload-photo-url"
                className="flex-1 bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              />
            </div>
            <div className="text-[10px] font-mono text-bm-dim mt-1">
              Files (≤2MB · PNG/JPG/WebP/GIF) are uploaded to object storage. URLs are kept as-is.
            </div>
            {form.photo_url && (
              <div className="mt-2 relative aspect-[3/2] overflow-hidden bg-bm-elevated" data-testid="upload-photo-preview">
                <img src={form.photo_url} alt="preview" className="w-full h-full object-cover" />
                <button
                  onClick={() => setForm({ ...form, photo_url: "" })}
                  className="absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-mono bg-black/70 text-white hover:bg-bm-danger"
                >
                  REMOVE
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="border-l-2 border-bm-danger pl-3 py-2 text-xs font-mono text-bm-danger" data-testid="upload-error">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-white/5">
            <button
              onClick={onClose}
              className="px-3 h-10 text-xs font-mono text-bm-muted border border-white/10 hover:text-white"
            >
              CANCEL
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="upload-submit"
              className="flex items-center gap-2 px-4 h-10 text-xs font-mono bg-bm-green text-black hover:bg-white disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {submitting ? "UPLOADING…" : "LOG OBSERVATION"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label-mono text-bm-dim mb-1 block">{label}</label>
      {children}
    </div>
  );
}
